import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Box, Typography, Button, Icon } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { AuthContext } from "src/utils/AuthContext.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { customEvent } from "src/utils/gtag.js";
import {
  getCalendarApiUrl,
  getWebcalFormat,
  handleOpenWebcal,
  handleSubscribeExperience,
} from "src/utils/Utils.js";
import { getApiRoute, getPageRoute } from "src/services";

const SubscriptionVerificationBanner = ({ calendar, calendarCreator, sx }) => {
  const { user, refetchAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [dismissed, setDismissed] = useState(false);

  const userCalendarAnalytics = useMemo(() => {
    const analytics = user?.verifiedCalendars?.find(
      (analytics) =>
        analytics.calendarId?.toString() === calendar?._id?.toString()
    );
    if (!analytics) return null;
    if (analytics) {
      // Check if this calendar has been verified within the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const lastVerified = new Date(analytics.lastVerifiedDate);
      analytics.isVerified = lastVerified >= thirtyDaysAgo;
    }
    return analytics;
  }, [calendar?._id, user?.verifiedCalendars]);

  useEffect(() => {
    // We should check that userCalendarAnalytics?.isVerified is strictly false as userCalendarAnalytics could be null.
    if (user && !dismissed && userCalendarAnalytics?.isVerified === false) {
      customEvent({
        name: "subscription_verification_banner_shown",
        category: "timeline_verification_banner",
        label: "timeline_verification_banner_load",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          calendar_handle: calendar?.handle,
        },
      });
    }
  }, [calendar?.handle, dismissed, user, userCalendarAnalytics?.isVerified]);

  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  const { mutateAsync: verifyCalendar, isLoading: isVerifying } = useMutation({
    mutationFn: async (calendarId) => {
      return await axios.post(
        getApiRoute("user", "VERIFY_CALENDAR"),
        { calendarId },
        { withCredentials: true }
      );
    },
  });

  const handleConfirm = useCallback(async () => {
    try {
      customEvent({
        name: "user_confirm_from_timline",
        category: "calendar_subscription",
        label: "confirm_from_timeline_banner",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          calendar_handle: calendar?.handle,
        },
      });

      if (user && calendar?._id) {
        await verifyCalendar(calendar._id);
        await refetchAuth(); // Refetch user to get updated verifiedCalendars
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          "Great! Your calendar subscription is verified.",
          "success"
        );
        setDismissed(true);
      }
    } catch (error) {
      console.error("Error verifying calendar:", error);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Failed to verify calendar. Please try again.",
        "error"
      );
    }
  }, [
    calendar,
    user,
    verifyCalendar,
    refetchAuth,
    showSnackBar,
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
  ]);

  const handleResubscribe = useCallback(() => {
    customEvent({
      name: "user_resubscribe_from_banner",
      category: "calendar_subscription",
      label: "resubscribe_from_timeline_banner",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });

    const calendarApiUrl = getCalendarApiUrl({ calendar, user });
    const defaultFn = () => {
      if (user?.provider === "google") {
        navigate(
          getPageRoute(
            "help",
            "SUBSCRIBING_CALENDAR_VERIFY",
            {
              handle: calendar.handle,
              calendarId: calendar._id,
            },
            { email: user.email, is_reminder: false }
          ),
          {
            state: { calendar },
          }
        );
      } else {
        handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
      }
    };

    handleSubscribeExperience({
      user,
      navigate,
      calendar,
      defaultFn,
    });
  }, [calendar, user, navigate]);

  // Don't show banner if dismissed or already verified
  if (
    dismissed ||
    !userCalendarAnalytics ||
    userCalendarAnalytics?.isVerified
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: theme.palette.background.paper,
        border: `2px solid ${theme.palette.border.primary}`,
        borderRadius: { xs: "12px", sm: "16px" },
        padding: { xs: 5, sm: 5 },
        mb: 3,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "50%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent)",
          animation: "glassReflection 8s ease-in-out infinite",
          transform: "skewX(-25deg)",
          pointerEvents: "none",
        },
        "@keyframes glassReflection": {
          "0%": { left: "-100%" },
          "25%": { left: "150%" },
          "100%": { left: "150%" },
        },
        ...sx,
      }}
    >
      {/* Close button */}
      <Icon
        baseClassName="material-symbols-outlined"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          fontSize: "20px",
          color: theme.palette.text.secondary,
          cursor: "pointer",
          fontWeight: 300,
          "&:hover": {
            color: theme.palette.text.primary,
          },
        }}
        onClick={() => setDismissed(true)}
      >
        close
      </Icon>

      <Typography
        variant="title"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        You've already added this calendar.
      </Typography>

      {/* Subtitle */}
      <Typography
        variant="body1"
        sx={{
          mt: 1,
          mb: 3,
          color: theme.palette.text.secondary,
          lineHeight: 1.5,
        }}
      >
        Are you seeing events in your calendar app?
      </Typography>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          size="shrinked"
          onClick={handleConfirm}
          disabled={isVerifying}
          sx={{
            flex: { xs: "1 1 auto", sm: "0 1 auto" },
          }}
        >
          Yes, I'm all set
        </Button>
        <Button
          variant="outlined"
          size="shrinked"
          onClick={handleResubscribe}
          sx={{
            flex: { xs: "1 1 auto", sm: "0 1 auto" },
          }}
        >
          No, help me subscribe
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionVerificationBanner;
